<template>
	<div class="base-textarea" :class="{ 'base-textarea--has-error': hasError }">
		<label v-if="label" class="base-textarea__label" :for="textareaId">
			{{ label }}
			<span v-if="required" class="base-textarea__required">*</span>
		</label>

		<textarea
			:id="textareaId"
			:value="modelValue"
			:placeholder="placeholder"
			:required="required"
			:disabled="disabled"
			:readonly="readonly"
			:rows="rows"
			:cols="cols"
			class="base-textarea__field"
			@input="handleInput"
			@blur="handleBlur"
			@focus="handleFocus"
		></textarea>

		<div v-if="error" class="base-textarea__error">
			{{ error }}
		</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

let textareaIdCounter = 0;

export default defineComponent({
	name: 'BaseTextarea',

	props: {
		modelValue: {
			type: String,
			default: '',
		},
		label: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		required: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		error: {
			type: String,
			default: '',
		},
		rows: {
			type: [String, Number],
			default: 3,
		},
		cols: {
			type: [String, Number],
			default: undefined,
		},
	},

	emits: ['update:modelValue', 'blur', 'focus'],

	setup(props, { emit }) {
		const textareaId = `base-textarea-${++textareaIdCounter}`;

		const hasError = computed(() => !!props.error);

		const handleInput = (event: Event) => {
			const target = event.target as HTMLTextAreaElement;
			emit('update:modelValue', target.value);
		};

		const handleBlur = (event: Event) => {
			emit('blur', event);
		};

		const handleFocus = (event: Event) => {
			emit('focus', event);
		};

		return {
			textareaId,
			hasError,
			handleInput,
			handleBlur,
			handleFocus,
		};
	},
});
</script>

<style scoped lang="scss">
.base-textarea {
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;

	&__label {
		margin-bottom: 0.5rem;
		color: #374151;
		font-size: 0.875rem;
		font-weight: 500;
	}

	&__required {
		color: #ef4444;
	}

	&__field {
		min-height: 80px;
		padding: 0.75rem;
		font-family: inherit;
		font-size: 0.875rem;
		resize: vertical;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;
		border: 1px solid #d1d5db;
		border-radius: 6px;

		&:focus {
			border-color: #3b82f6;
			outline: none;
			box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
		}

		&:disabled {
			color: #6b7280;
			resize: none;
			cursor: not-allowed;
			background-color: #f9fafb;
		}

		&::placeholder {
			color: #9ca3af;
		}
	}

	&__error {
		margin-top: 0.25rem;
		color: #ef4444;
		font-size: 0.75rem;
	}

	&--has-error {
		.base-textarea__field {
			border-color: #ef4444;

			&:focus {
				border-color: #ef4444;
				box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
			}
		}
	}
}
</style>
